<template>
  <div class="table-container">
    <div class="table-header">
      <el-button @click="handleAdd" type="primary" size="default" class="add-btn">
        <el-icon><Plus /></el-icon>
        {{ $t('UpgradeRulesManagement.addRule') }}
      </el-button>
    </div>
    <!-- 调试标记：如果看到这个，说明修改已生效 -->
    <el-table :data="data" v-loading="loading" style="width: 100%" border>
      <el-table-column prop="id" :label="$t('UpgradeRulesManagement.serialNumber')" width="80" align="center"/>
      
      <el-table-column prop="typeText" :label="$t('UpgradeRulesManagement.type')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="getTypeTagType(scope.row.type)">
            {{ scope.row.typeText }}
          </el-tag>
        </template>
      </el-table-column>
       
      
      <el-table-column prop="version" :label="$t('UpgradeRulesManagement.version')" min-width="80" show-overflow-tooltip/>
      
      <el-table-column prop="enableText" :label="$t('UpgradeRulesManagement.enableStatus')" width="120" align="center">
        <template #default="scope">
          <el-tag :type="scope.row.enable ? 'success' : 'danger'">
            {{ scope.row.enableText }}
          </el-tag>
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('UpgradeRulesManagement.createdUserEmail')" min-width="150" align="center" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.createdUser && scope.row.createdUser.email ? scope.row.createdUser.email : '-' }}
        </template>
      </el-table-column>
      
      <!-- 用户账号列 -->
      <el-table-column :label="$t('UpgradeRulesManagement.userAccount')" min-width="150" align="center" show-overflow-tooltip>
        <template #default="scope">
          {{ scope.row.user && scope.row.user.email ? scope.row.user.email : '-' }}
        </template>
      </el-table-column>
      
      <el-table-column prop="createdAt" :label="$t('UpgradeRulesManagement.createdAt')" width="180" align="center">
        <template #default="scope">
          {{ formatDateTime(scope.row.createdAt) }}
        </template>
      </el-table-column>
      
      <el-table-column prop="updatedAt" :label="$t('UpgradeRulesManagement.updatedAt')" width="180" align="center">
        <template #default="scope">
          {{ scope.row.updatedAt ? formatDateTime(scope.row.updatedAt) : '-' }}
        </template>
      </el-table-column>
      
      <el-table-column :label="$t('UpgradeRulesManagement.operation')" width="240" align="center" fixed="right">
        <template #default="scope">
          <div class="action-buttons">
            <el-button 
              @click="handleEdit(scope.row)" 
              type="success" 
              size="small"
              class="edit-btn" style="margin-right: 8px;">
              <el-icon><Edit /></el-icon>
              {{ $t('UpgradeRulesManagement.edit') }}
            </el-button>
            <el-button 
              @click="handleToggleStatus(scope.row)" 
              type="info" 
              size="small"
              class="toggle-btn">
              {{ scope.row.enable ? $t('UpgradeRulesManagement.disable') : $t('UpgradeRulesManagement.enable') }}
            </el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <!-- 编辑对话框 -->
    <el-dialog :model-value="editDialogVisible" :title="dialogTitle" width="600px" :show-close="true" @close="closeEditDialog" @update:model-value="editDialogVisible = $event">
      <el-form :model="editForm" :rules="editRules" ref="editFormRef" label-width="120px">
        <el-form-item :label="$t('UpgradeRulesManagement.type')" prop="type">
          <el-select v-model="editForm.type" style="width: 100%">
            <el-option :label="$t('UpgradeRulesManagement.typeFirmware')" value="0" />
            <el-option :label="$t('UpgradeRulesManagement.typeParameter')" value="1" />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('UpgradeRulesManagement.userAccount')" prop="userId">
          <el-select 
            v-model="editForm.userId" 
            style="width: 100%"
            :loading="usersLoading"
            filterable
            clearable
            remote
            :remote-method="loadUsersList"
            :placeholder="$t('UpgradeRulesManagement.userAccountPlaceholder')"
          >
            <el-option 
              v-for="user in usersList" 
              :key="user.id" 
              :label="user.email || user.name || user.id" 
              :value="user.id" 
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('UpgradeRulesManagement.version')" prop="version">
          <el-input v-model="editForm.version" :placeholder="$t('UpgradeRulesManagement.versionPlaceholder')" maxlength="255" show-word-limit />
        </el-form-item>
        <el-form-item :label="$t('UpgradeRulesManagement.enableStatus')" prop="enable">
          <el-switch v-model="editForm.enable" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeEditDialog" type="default">{{ $t('UpgradeRulesManagement.cancel') }}</el-button>
        <el-button @click="submitEdit" :loading="editLoading" type="primary">{{ $t('UpgradeRulesManagement.save') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { updateUpgradeRule, addUpgradeRule, getUsersSimple } from '@/api/api';
import dialogMixin from '@/mixins/dialogMixin';

export default {
  mixins: [dialogMixin],
  props: {
    data: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    dialogTitle() {
      return this.isAdding ? this.$t('UpgradeRulesManagement.addRule') : this.$t('UpgradeRulesManagement.editRule');
    }
  },
  data() {
    return {
      editDialogVisible: false,
      isAdding: false,
      currentRule: {},
      editForm: {
        type: '0',
        version: '',
        enable: true,
        userId: '' // 新增账号ID字段
      },
      editRules: {
        type: [
          { required: true, message: this.$t('UpgradeRulesManagement.typeRequired'), trigger: 'change' }
        ],
        version: [
          { required: true, message: this.$t('UpgradeRulesManagement.versionRequired'), trigger: 'blur' },
          { min: 1, max: 255, message: this.$t('UpgradeRulesManagement.versionLength'), trigger: 'blur' }
        ],
        userId: [
          { required: true, message: this.$t('UpgradeRulesManagement.userAccountRequired'), trigger: 'change' }
        ]
      },
      editLoading: false,
      usersList: [], // 账号列表数据
      usersLoading: false // 账号数据加载状态
    };
  },
  mounted() {
    this.loadSubPermissions();
    // 组件挂载时加载账号列表
    this.loadUsersList();
  },
  methods: {
    // 加载账号列表数据，支持搜索
    async loadUsersList(query) {
      try {
        this.usersLoading = true;
        const res = await getUsersSimple();
        
        if (res && res.code === 0 && Array.isArray(res.data)) {
          if (query) {
            // 如果有搜索词，在本地过滤数据
            this.usersList = res.data.filter(user => 
              // 在email、name或id中搜索
              (user.email && user.email.toLowerCase().includes(query.toLowerCase())) ||
              (user.name && user.name.toLowerCase().includes(query.toLowerCase())) ||
              (user.id && user.id.toString().includes(query))
            );
          } else {
            // 如果没有搜索词，显示全部数据
            this.usersList = res.data;
          }
        } else {
          ElMessage.error(this.$t('UpgradeRulesManagement.loadUsersFailed'));
          this.usersList = [];
        }
      } catch (error) {
        console.error('加载账号列表失败:', error);
        ElMessage.error(this.$t('UpgradeRulesManagement.loadUsersFailed'));
        this.usersList = [];
      } finally {
        this.usersLoading = false;
      }
    },

    // 获取类型标签类型
    getTypeTagType(type) {
      const typeMap = {
        0: 'success',
        1: 'info'
      };
      return typeMap[type] || 'info';
    },

    // 格式化日期时间
    formatDateTime(dateTime) {
      if (!dateTime) return '-';
      const date = new Date(dateTime);
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },

    // 处理编辑
    handleEdit(row) {
      this.isAdding = false;
      // 深拷贝确保获取最新状态
      this.currentRule = JSON.parse(JSON.stringify(row));
      this.editForm = {
        type: row.type.toString(),
        version: row.version,
        enable: row.enable === 1 || row.enable === true, // 将数字或布尔值转换为布尔值
        userId: row.userId || ''
      };
      this.editDialogVisible = true;
    },
    
    // 处理新增
    handleAdd() {
      this.isAdding = true;
      this.currentRule = {};
      this.editForm = {
        type: '0',
        version: '',
        enable: true,
        userId: ''
      };
      // 重新加载账号列表以获取最新数据
      this.loadUsersList();
      this.editDialogVisible = true;
    },

    // 处理切换状态
    async handleToggleStatus(row) {
      try {
        const newStatus = !row.enable;
        // 调用定制账号接口来切换状态
        const res = await updateUpgradeRule(row.id, {
          type: row.type,
          version: row.version,
          enable: newStatus ? 1 : 0, // 确保状态值正确转换为数字
          user_id: row.userId || ''
        });
        
        if (res && res.code === 0) {
          ElMessage.success(this.$t('UpgradeRulesManagement.toggleSuccess'));
          // 刷新数据
          this.$emit('refresh');
        } else {
          ElMessage.error(res && res.message ? res.message : this.$t('UpgradeRulesManagement.toggleFailed'));
        }
      } catch (error) {
        console.error('切换状态失败:', error);
        ElMessage.error(this.$t('UpgradeRulesManagement.toggleFailed'));
      }
    },

    // 关闭编辑对话框
    closeEditDialog() {
      this.editDialogVisible = false;
      this.currentRule = {};
      this.editForm = {
        type: '0',
        version: '',
        enable: true,
        userId: ''
      };
    },
    
    // 提交编辑
    async submitEdit() {
      try {
        // 验证表单
        if (!this.$refs.editFormRef) {
          ElMessage.error(this.$t('UpgradeRulesManagement.formValidationError'));
          return;
        }
        
        await this.$refs.editFormRef.validate();
        
        this.editLoading = true;
        
        let res;
        if (this.isAdding) {
          // 调用新建接口
          res = await addUpgradeRule({
            type: parseInt(this.editForm.type),
            version: this.editForm.version,
            enable: this.editForm.enable ? 1 : 0, // 确保状态值正确转换为数字
            user_id: this.editForm.userId
          });
        } else {
          // 调用更新接口
          res = await updateUpgradeRule(this.currentRule.id, {
            type: parseInt(this.editForm.type),
            version: this.editForm.version,
            enable: this.editForm.enable ? 1 : 0, // 确保状态值正确转换为数字
            user_id: this.editForm.userId
          });
        }
        
        if (res && res.code === 0) {
          if (this.isAdding) {
            ElMessage.success(this.$t('UpgradeRulesManagement.addRuleSuccess'));
          } else {
            ElMessage.success(this.$t('UpgradeRulesManagement.saveSuccess'));
          }
          this.closeEditDialog();
          // 刷新数据
          this.$emit('refresh');
        } else {
          if (this.isAdding) {
            ElMessage.error(res && res.message ? res.message : this.$t('UpgradeRulesManagement.addRuleFailed'));
          } else {
            ElMessage.error(res && res.message ? res.message : this.$t('UpgradeRulesManagement.saveFailed'));
          }
        }
      } catch (error) {
        if (error !== false) { // 不是表单验证错误
          console.error('保存失败:', error);
          ElMessage.error(this.$t('UpgradeRulesManagement.saveFailed'));
        }
      } finally {
        this.editLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  margin: 20px;
}

.table-header {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.search-container {
  display: flex;
  align-items: center;
  margin-right: auto;
}

.add-btn {
  background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
  border: none;
  box-shadow: 0 2px 4px rgba(103, 194, 58, 0.3);
  transition: all 0.3s ease;
}

.add-btn:hover {
  background: linear-gradient(135deg, #5daf34 0%, #73c558 100%);
  box-shadow: 0 4px 8px rgba(103, 194, 58, 0.4);
  transform: translateY(-1px);
}

  .edit-btn {
    background: linear-gradient(135deg, #67c23a 0%, #85ce61 100%);
    border: none;
    box-shadow: 0 2px 4px rgba(103, 194, 58, 0.3);
    transition: all 0.3s ease;
  }

  .edit-btn:hover {
    background: linear-gradient(135deg, #5daf34 0%, #73c558 100%);
    box-shadow: 0 4px 8px rgba(103, 194, 58, 0.4);
    transform: translateY(-1px);
  }

  .toggle-btn {
    background: linear-gradient(135deg, #909399 0%, #a6a9ad 100%);
    border: none;
    box-shadow: 0 2px 4px rgba(144, 147, 153, 0.3);
    transition: all 0.3s ease;
  }

  .toggle-btn:hover {
    background: linear-gradient(135deg, #82848a 0%, #9ca0a4 100%);
    box-shadow: 0 4px 8px rgba(144, 147, 153, 0.4);
    transform: translateY(-1px);
  }
  
  .action-buttons {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  .el-button {
    border-radius: 6px;
    font-weight: 500;
  }

  .el-button .el-icon {
    margin-right: 4px;
  }
</style>
